<template>
  <Transition name="fade">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      @click.self="emit('close')"
    >
      <div
        class="relative flex h-full max-h-[80vh] min-h-0 w-full max-w-[1120px] gap-3 md:gap-8 overflow-hidden rounded-[20px] border border-tertiary bg-secondary p-3 md:p-8 shadow-2xl"
      >
        <!-- Left side: Profile (im Demo-Modus ausgeblendet — keine echten Nutzerdaten) -->
        <div
          v-if="!demoMode"
          class="flex w-[280px] shrink-0 flex-col gap-6"
        >
          <div
            class="group relative flex aspect-square cursor-pointer items-center justify-center overflow-hidden rounded-[20px] border border-tertiary bg-primary"
          >
            <span class="font-bold text-text-main/50 transition-opacity group-hover:opacity-0">
              Profilbild
            </span>

            <div
              class="absolute inset-0 hidden items-center justify-center bg-black/20 font-bold text-white opacity-0 transition-opacity group-hover:flex group-hover:opacity-100"
            >
              Ändern
            </div>
          </div>

          <div class="rounded-[20px] border border-tertiary bg-primary p-6 text-sm leading-relaxed">
            <p>
              <span class="font-bold">Name:</span>
              Username
            </p>

            <p class="mt-4 font-bold">
              Gruppen:
            </p>

            <ul class="mt-2 list-none space-y-1 text-text-main/80">
              <li v-if="myGroups.length === 0" class="text-sm italic">Keine Gruppen</li>
              <li v-for="group in myGroups" :key="group.id">{{ group.name }}</li>
            </ul>
          </div>
          <div class="align-center">
            <button
              type="button"
              class="mb-3 flex h-10 w-full cursor-pointer items-center justify-center rounded-md border border-tertiary bg-accent text-lg font-bold text-white shadow-sm transition-colors hover:bg-accent/80"
              @click="emit('open-groups')"
            >
              Gruppen verwalten
            </button>
            <button
              type="button"
              class="flex h-10 w-30 cursor-pointer items-center justify-center rounded-md border border-tertiary bg-secondary text-lg font-bold shadow-sm transition-colors hover:bg-accent hover:text-white"
              @click="logoutUser()"
            >
              Logout
            </button>
          </div>
        </div>

        <!-- Right side: Settings -->
        <div class="flex min-h-0 min-w-0 flex-1 flex-col gap-6">
          <div
            class="flex items-center justify-between rounded-[20px] border border-tertiary bg-primary px-6 py-8"
          >
            <span class="text-2xl font-bold"> Zoomstufe: {{ currentFontSize }} </span>

            <div class="flex gap-3">
              <button
                type="button"
                class="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-tertiary bg-secondary text-lg font-bold shadow-sm transition-colors hover:bg-accent hover:text-white"
                @click="changeFontSize(-1)"
              >
                -
              </button>

              <button
                type="button"
                class="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-tertiary bg-secondary text-lg font-bold shadow-sm transition-colors hover:bg-accent hover:text-white"
                @click="changeFontSize(1)"
              >
                +
              </button>
            </div>
          </div>

          <div
            class="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[20px] border border-tertiary bg-primary p-6"
          >
            <h3 class="mb-4 shrink-0 text-xl font-bold">
              Farboptionen:
            </h3>
            <ThemeShowcase
              class="min-h-0 flex-1"
              :themes="activeThemes"
            />
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import ThemeShowcase from '@/components/ThemeShowcase.vue'
import { computed, ref, watch } from 'vue'
import { currentThemesRef } from '@/composables/useThemeNew.ts'
import {API_BASE_URL} from "@/config.ts";

const myGroups = ref<Record<string, unknown>[]>([])

const props = defineProps<{
  isOpen: boolean
  currentFontSize: number
  /** Demo-Modus: blendet die (nicht vorhandenen) Nutzerdaten links aus. */
  demoMode?: boolean
}>()

const emit = defineEmits<{
  (event: 'close'): void
  (event: 'open-groups'): void
  (event: 'update:fontSize', size: number): void
}>()

// Reaktive Themes — initialisiert mit Fallback, wird von useThemeNew aktualisiert wenn themes.json lädt
const activeThemes = computed(() => currentThemesRef.value)

async function loadMyGroups() {
  try {
    const res = await fetch(`${API_BASE_URL}/api/groups/my-groups`, {
      credentials: 'include',
    })
    if (res.ok) {
      myGroups.value = await res.json()
    }
  } catch (error) {
    console.error('Error fetching my groups:', error)
  }
}

watch(
  () => props.isOpen,
  (newVal) => {
    if (newVal && !props.demoMode) {
      loadMyGroups()
    }
  }
)

function changeFontSize(delta: number) {
  const newSize = Math.max(6, Math.min(80, props.currentFontSize + delta))

  emit('update:fontSize', newSize)
}

async function logoutUser() {
  try {
    const response = await fetch(`${API_BASE_URL}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    })
    if (!response.ok) {
      console.log(response.status + ": " + response.statusText)
      return
    }
    window.location.href = 'index.html'
  } catch (error) {
    console.error('error:', error)
  }
}
</script>
